import { useId, useMemo, useRef, type KeyboardEvent, type ReactNode } from "react";
import { Box, Group, Text, UnstyledButton } from "@mantine/core";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { parseJsonText } from "@utils/jsonText";
import { spacePx } from "@xplatform/design-system";
import "./CodeEditor.scss";

interface CodeEditorProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    /** `json` adds line numbers, live validation and formatting; `text` wraps long lines. */
    language: "json" | "text";
    required?: boolean;
    placeholder?: string;
    description?: ReactNode;
    /** Shown under the editor (e.g. "required"); JSON errors are computed here. */
    error?: string | null;
}

const INDENT = "  ";

/**
 * A plain-textarea code editor: monospace, no spellcheck, Tab indents, and for
 * JSON a line-number gutter, a live parse check pointing at the line and column
 * of the first error, and a one-click formatter. Deliberately dependency-free;
 * prompts and schemas are the only code this module edits.
 */
export const CodeEditor = ({
    label,
    value,
    onChange,
    language,
    required,
    placeholder,
    description,
    error,
}: CodeEditorProps) => {
    const { t } = useTranslation();
    const id = useId();
    const gutterRef = useRef<HTMLPreElement>(null);
    const isJson = language === "json";

    const lineCount = value ? value.split("\n").length : 1;
    const gutter = useMemo(
        () => Array.from({ length: lineCount }, (_, i) => i + 1).join("\n"),
        [lineCount],
    );
    const parsed = useMemo(() => (isJson ? parseJsonText(value) : null), [isJson, value]);
    const jsonError = parsed && !parsed.ok ? parsed.error : null;

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== "Tab" || event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return;
        event.preventDefault();
        const area = event.currentTarget;
        const { selectionStart, selectionEnd } = area;
        onChange(value.slice(0, selectionStart) + INDENT + value.slice(selectionEnd));
        requestAnimationFrame(() => {
            area.selectionStart = area.selectionEnd = selectionStart + INDENT.length;
        });
    };

    const format = () => {
        if (parsed?.ok && parsed.value) onChange(JSON.stringify(parsed.value, null, 2));
    };

    const errorText = jsonError
        ? jsonError.line
            ? t("admin.flowConfigs.editor.jsonErrorAt", { line: jsonError.line, column: jsonError.column, message: jsonError.message })
            : t("admin.flowConfigs.editor.jsonError", { message: jsonError.message })
        : error;

    return (
        <Box className="code-editor">
            <Group justify="space-between" align="flex-end" mb="xs" wrap="nowrap">
                <Box>
                    <Text component="label" htmlFor={id} size="sm" fw={500}>
                        {label}
                        {required && <Text component="span" c="red"> *</Text>}
                    </Text>
                    {description && <Text size="xs" c="dimmed">{description}</Text>}
                </Box>
                <Group gap="sm" wrap="nowrap">
                    {isJson && value.trim() && !jsonError && (
                        <Group gap={spacePx[1]} wrap="nowrap">
                            <IconCircleCheck size={14} color="var(--mantine-color-green-7)" />
                            <Text size="xs" c="green.7">{t("admin.flowConfigs.editor.validJson")}</Text>
                        </Group>
                    )}
                    {isJson && (
                        <UnstyledButton
                            onClick={format}
                            disabled={!parsed?.ok || !parsed.value}
                            className="code-editor__action"
                        >
                            <Text size="xs" fw={500}>{t("admin.flowConfigs.editor.format")}</Text>
                        </UnstyledButton>
                    )}
                    <Text size="xs" c="dimmed">
                        {t("admin.flowConfigs.editor.stats", { count: lineCount, characters: value.length })}
                    </Text>
                </Group>
            </Group>
            <Box className="code-editor__frame" data-invalid={errorText ? true : undefined} data-language={language}>
                {isJson && (
                    <pre ref={gutterRef} className="code-editor__gutter" aria-hidden>
                        {gutter}
                    </pre>
                )}
                <textarea
                    id={id}
                    className="code-editor__input"
                    value={value}
                    placeholder={placeholder}
                    spellCheck={false}
                    autoComplete="off"
                    wrap={isJson ? "off" : "soft"}
                    required={required}
                    aria-invalid={errorText ? true : undefined}
                    onChange={(event) => onChange(event.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    onScroll={(event) => {
                        if (gutterRef.current) gutterRef.current.scrollTop = event.currentTarget.scrollTop;
                    }}
                />
            </Box>
            {errorText && (
                <Group gap={spacePx[1]} mt={spacePx[1]} wrap="nowrap" role="alert">
                    <IconAlertTriangle size={14} color="var(--mantine-color-red-7)" />
                    <Text size="xs" c="red.7">{errorText}</Text>
                </Group>
            )}
        </Box>
    );
};
